import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { AppLayout } from '../components/layout';
import { Card, Button, Select, AppLoader } from '../components/ui';
import useProvidersStore, { Provider } from '../stores/providersStore';
import { providersApi, extractPaginationInfo, getPrimaryLocation } from '../api';

const PAGE_SIZE = 20;

const Providers = () => {
	const navigate = useNavigate();
	const [selectedBusiness, setSelectedBusiness] = useState('all');
	const [currentPage, setCurrentPage] = useState(0);

	// Store state
	const providers = useProvidersStore(state => state.providers);
	const pagination = useProvidersStore(state => state.pagination);
	const loadingState = useProvidersStore(state => state.loadingState);
	const error = useProvidersStore(state => state.error);
	const shouldInvalidate = useProvidersStore(state => state.shouldInvalidate);

	// Store actions
	const setProviders = useProvidersStore(state => state.setProviders);
	const appendProviders = useProvidersStore(state => state.appendProviders);
	const setLoadingState = useProvidersStore(state => state.setLoadingState);
	const setError = useProvidersStore(state => state.setError);
	const clearInvalidation = useProvidersStore(state => state.clearInvalidation);

	// Intersection observer for infinite scroll
	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: '100px',
	});

	const businesses = [
		{ label: 'All Providers', value: 'all' },
		{ label: 'Trendit', value: 'trendit' },
		{ label: 'BookForBraids', value: 'bookforbraids' },
		{ label: '200Below', value: '200below' },
	];

	// Fetch providers
	const fetchProviders = useCallback(
		async (page: number, append: boolean = false) => {
			try {
				setLoadingState(append ? 'loading-more' : 'loading');

				const response = await providersApi.list({
					page,
					size: PAGE_SIZE,
					business: selectedBusiness !== 'all' ? selectedBusiness : undefined,
				});

				const paginationInfo = extractPaginationInfo(response);

				if (append) {
					appendProviders(response.content, paginationInfo);
				} else {
					setProviders(response.content, paginationInfo);
				}

				setCurrentPage(page);
			} catch (err) {
				setError(err instanceof Error ? err.message : 'Failed to load providers');
			}
		},
		[selectedBusiness, setProviders, appendProviders, setLoadingState, setError]
	);

	// Initial fetch and invalidation handling
	useEffect(() => {
		if (shouldInvalidate) {
			clearInvalidation();
			fetchProviders(0);
		}
	}, [shouldInvalidate, clearInvalidation, fetchProviders]);

	// Fetch on business filter change
	useEffect(() => {
		// fetchProviders(0); // Uncomment when API is ready
	}, [selectedBusiness]);

	// Load more when scrolling to bottom
	useEffect(() => {
		if (inView && loadingState === 'idle' && pagination && !pagination.last) {
			fetchProviders(currentPage + 1, true);
		}
	}, [inView, loadingState, pagination, currentPage, fetchProviders]);

	// Handle row click
	const handleRowClick = (provider: Provider) => {
		navigate(`/providers/${provider.id}`);
	};

	// Handle retry
	const handleRetry = () => {
		fetchProviders(0);
	};

	// Render loading state
	const renderLoading = () => <AppLoader message="Loading providers" variant="inline" />;

	// Render empty state
	const renderEmptyState = () => (
		<div className="text-center py-16">
			<div className="text-6xl mb-4 opacity-30">👥</div>
			<h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
				No Providers Found
			</h3>
			<p className="text-gray-500 dark:text-gray-400 mb-6">
				{selectedBusiness !== 'all'
					? 'Try selecting a different business filter'
					: 'Get started by adding your first provider'}
			</p>
			<Button variant="primary" onClick={() => navigate('/providers/new')}>
				+ Add New Provider
			</Button>
		</div>
	);

	// Render error state
	const renderErrorState = () => (
		<div className="text-center py-16">
			<div className="text-6xl mb-4">⚠️</div>
			<h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
				Failed to Load Providers
			</h3>
			<p className="text-gray-500 dark:text-gray-400 mb-6">
				{error || 'An unexpected error occurred'}
			</p>
			<Button variant="primary" onClick={handleRetry}>
				Try Again
			</Button>
		</div>
	);

	// Render loading more indicator
	const renderLoadingMore = () => (
		<div className="flex items-center justify-center py-6 gap-2">
			<div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
			<span className="text-sm text-gray-500 dark:text-gray-400">Loading more...</span>
		</div>
	);

	return (
		<AppLayout>
			{/* Filter Dropdown - Outside Card */}
			<div className="mb-6">
				<Select
					value={selectedBusiness}
					onChange={e => setSelectedBusiness(e.target.value)}
					className="min-w-[160px]"
					inline
				>
					{businesses.map(biz => (
						<option key={biz.value} value={biz.value}>
							{biz.label}
						</option>
					))}
				</Select>
			</div>

			<Card>
				{/* Header */}
				<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
					<h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 m-0">
						Providers
					</h2>
					<Button
						variant="primary"
						onClick={() => navigate('/providers/new')}
						className="w-full sm:w-auto shrink-0"
					>
						+ Add New Provider
					</Button>
				</div>

				{/* Content */}
				{loadingState === 'loading' ? (
					renderLoading()
				) : loadingState === 'error' ? (
					renderErrorState()
				) : providers.length === 0 ? (
					renderEmptyState()
				) : (
					<>
						{/* Table */}
						<div className="overflow-x-auto -mx-[18px] px-[18px]">
							<table className="w-full border-collapse">
								<thead>
									<tr>
										<th className="text-left p-3 text-[13px] font-semibold text-gray-500 dark:text-gray-400 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
											Name
										</th>
										<th className="text-left p-3 text-[13px] font-semibold text-gray-500 dark:text-gray-400 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
											State
										</th>
										<th className="text-left p-3 text-[13px] font-semibold text-gray-500 dark:text-gray-400 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
											Total Appointments
										</th>
										<th className="text-left p-3 text-[13px] font-semibold text-gray-500 dark:text-gray-400 border-b-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
											Total Revenue
										</th>
									</tr>
								</thead>
								<tbody>
									{providers.map(provider => (
										<tr
											key={provider.id}
											onClick={() => handleRowClick(provider)}
											className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50"
										>
											<td className="p-3 border-t border-gray-100 dark:border-gray-700">
												<div className="font-semibold text-gray-900 dark:text-gray-100">
													{provider.name}
												</div>
											</td>
											<td className="p-3 border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300">
												{getPrimaryLocation(provider.locations)?.state ||
													'-'}
											</td>
											<td className="p-3 border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300">
												{provider.totalAppointments}
											</td>
											<td className="p-3 border-t border-gray-100 dark:border-gray-700">
												<span className="font-bold text-[#667eea]">
													${provider.totalRevenue.toLocaleString()}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						{/* Infinite scroll trigger */}
						{pagination && !pagination.last && (
							<div ref={loadMoreRef}>
								{loadingState === 'loading-more' && renderLoadingMore()}
							</div>
						)}

						{/* End of list indicator */}
						{pagination?.last && providers.length > 0 && (
							<div className="text-center py-4 text-sm text-gray-400 dark:text-gray-500">
								Showing all {pagination.totalElements} providers
							</div>
						)}
					</>
				)}
			</Card>

			<div className="mt-6 text-sm text-gray-500 dark:text-gray-400 text-center">
				© 2025 Beauty Business Portal. All rights reserved.
			</div>
		</AppLayout>
	);
};

export default Providers;
